
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from "@/components/ui/dialog";
import { 
  Sparkles, 
  MessageSquare, 
  Edit3, 
  BarChart3, 
  Heart, 
  History,
  ChevronRight,
  ChevronLeft,
  X
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface TourStep {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  highlight?: string;
}

const tourSteps: TourStep[] = [
  {
    title: "Welcome to Product Copilot! 🚀",
    description: "Your AI-powered assistant for creating professional product documents, PRDs, specs, and more. Let's take a quick tour to get you started.",
    icon: Sparkles,
    highlight: "AI-powered productivity"
  },
  {
    title: "Choose Your Template",
    description: "Browse our collection of expert-crafted templates for different document types. Each template is optimized for specific use cases like PRDs, user stories, or project specs.",
    icon: MessageSquare,
    highlight: "Professional templates"
  },
  {
    title: "Edit Before You Send",
    description: "When you select a template, its content automatically loads into the chat input. You can edit, customize, or add specific details before sending to the AI.",
    icon: Edit3,
    highlight: "Fully customizable"
  },
  {
    title: "Track Your Usage",
    description: "Monitor your monthly message limits in the dashboard. Free users get 5 messages, Plus users get 100, and Pro users get 300 messages per month.",
    icon: BarChart3,
    highlight: "Usage tracking"
  },
  {
    title: "Save Your Favorites",
    description: "Found a template you love? Save it to your favorites for quick access later. Build your personal library of go-to templates.",
    icon: Heart,
    highlight: "Personal library"
  },
  {
    title: "Access Chat History",
    description: "All your conversations are automatically saved. Return to previous chats anytime to continue working on documents or reference past interactions.",
    icon: History,
    highlight: "Never lose work"
  }
];

interface ProductCopilotTourProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ProductCopilotTour = ({ open, onOpenChange }: ProductCopilotTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const { user } = useAuth();

  const currentTourStep = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    if (!user) return;
    
    setIsCompleting(true);
    try {
      await supabase.rpc('mark_copilot_tour_as_seen');
      onOpenChange(false);
    } catch (error) {
      console.error('Error marking tour as completed:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 rounded-full p-2">
                <currentTourStep.icon className="h-5 w-5 text-primary" />
              </div>
              <Badge variant="outline">{currentStep + 1} of {tourSteps.length}</Badge>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleSkip}
              className="text-muted-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <DialogTitle className="text-left">{currentTourStep.title}</DialogTitle>
          <DialogDescription className="text-left">
            {currentTourStep.description}
          </DialogDescription>
        </DialogHeader>

        {currentTourStep.highlight && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                {currentTourStep.highlight}
              </span>
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={isFirstStep}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleSkip}>
              Skip Tour
            </Button>
            <Button 
              onClick={handleNext}
              disabled={isCompleting}
              className="flex items-center gap-2"
            >
              {isLastStep ? 'Get Started' : 'Next'}
              {!isLastStep && <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductCopilotTour;
